import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Download, FileText, Quote, ExternalLink, Mail } from "lucide-react";
import { api, assetUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { MetricsPanel, ShareMenu, trackMetric } from "@/components/ArticleMetrics";
import { cleanDegree } from "@/components/ArticleCards";
import ScholarMeta from "@/components/ScholarMeta";

export default function ArticleDetail() {
  const { t } = useI18n();
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const viewedSlug = useRef(null);

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/articles/${slug}`).then((r) => {
      setA(r.data);
      setLoading(false);
    }).catch((e) => {
      setError("Article not found.");
      setLoading(false);
    });
    // Count an HTML view once per slug (guard against StrictMode double-invoke)
    if (viewedSlug.current !== slug) {
      viewedSlug.current = slug;
      trackMetric(slug, "view");
    }
  }, [slug]);

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-24 font-serif text-[#001F3F]/70 text-center">{t("common.loading")}</div>;
  if (error || !a) return <div className="max-w-5xl mx-auto px-6 py-24 font-serif text-[#001F3F]/70 text-center">{error || "Not found"}</div>;

  const corresp = (a.authors || []).find((au) => au.corresponding);

  return (
    <article data-testid="article-detail" className="bg-white">
      <ScholarMeta article={a} />
      <div className="bg-[#F5F7FA] border-b border-gray-200 py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-[11px] font-sans tracking-[0.2em] uppercase font-semibold text-[#2BA6A6] mb-4">
            {a.article_type}
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#001F3F] leading-tight tracking-tight" data-testid="article-title">{a.title}</h1>
          <div className="mt-6 font-serif text-base text-[#001F3F]/85" data-testid="article-authors">
            {(a.authors || []).map((au, i) => {
              const name = [au.first_name, au.middle_name, au.last_name, au.second_last_name].filter(Boolean).join(" ");
              return (
                <span key={au.id || i}>
                  {name}{cleanDegree(au.academic_degree) ? `, ${cleanDegree(au.academic_degree)}` : ""}
                  {au.corresponding ? <sup className="text-[#2BA6A6]">*</sup> : null}
                  {i < a.authors.length - 1 ? "; " : ""}
                </span>
              );
            })}
          </div>
          <div className="mt-3 text-xs font-sans text-gray-500">
            {a.volume && <span>Vol. {a.volume}, Issue {a.issue} · </span>}
            {a.published_date || a.year}
            {a.pages && <span> · pp. {a.pages}</span>}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <section>
            <h2 className="font-sans text-[11px] tracking-[0.25em] uppercase font-semibold text-[#2BA6A6] mb-3">{t("article.abstract")}</h2>
            {a.structured_abstract ? (
              <div className="space-y-3 font-serif text-base text-[#0A1118] leading-relaxed">
                {a.abstract_background && <p><strong className="text-[#001F3F]">Background.</strong> {a.abstract_background}</p>}
                {a.abstract_methods && <p><strong className="text-[#001F3F]">Methods.</strong> {a.abstract_methods}</p>}
                {a.abstract_results && <p><strong className="text-[#001F3F]">Results.</strong> {a.abstract_results}</p>}
                {a.abstract_conclusions && <p><strong className="text-[#001F3F]">Conclusions.</strong> {a.abstract_conclusions}</p>}
              </div>
            ) : (
              <p className="font-serif text-base text-[#0A1118] leading-relaxed">{a.abstract}</p>
            )}
          </section>

          {a.keywords && a.keywords.length > 0 && (
            <section className="mt-10">
              <h2 className="font-sans text-[11px] tracking-[0.25em] uppercase font-semibold text-[#2BA6A6] mb-3">{t("article.keywords")}</h2>
              <div className="flex flex-wrap gap-2">
                {a.keywords.map((k, i) => (
                  <span key={i} className="px-3 py-1 bg-[#F5F7FA] border border-gray-200 text-xs font-sans text-[#001F3F]">{k}</span>
                ))}
              </div>
            </section>
          )}

          {/* Main text sections (if present) */}
          {["introduction", "methods", "results", "discussion", "conclusion"].map((sec) => (
            a[sec] && (
              <section key={sec} className="mt-10">
                <h2 className="font-sans text-[11px] tracking-[0.25em] uppercase font-semibold text-[#2BA6A6] mb-3">
                  {sec.toUpperCase()}
                </h2>
                <div className="font-serif text-base text-[#0A1118] leading-relaxed whitespace-pre-line">{a[sec]}</div>
              </section>
            )
          ))}

          {/* Figures */}
          {a.figures && a.figures.length > 0 && (
            <section className="mt-10" data-testid="article-figures">
              <h2 className="font-sans text-[11px] tracking-[0.25em] uppercase font-semibold text-[#2BA6A6] mb-4">{t("article.figures") || "FIGURES"}</h2>
              <div className="space-y-8">
                {a.figures.filter((f) => f.image_url).map((f, i) => (
                  <figure key={i} className="border border-gray-200 bg-[#F5F7FA]" data-testid={`article-figure-${i}`}>
                    <img src={assetUrl(f.image_url)} alt={f.title || `Figure ${f.number}`} className="w-full object-contain bg-white" loading="lazy" />
                    <figcaption className="px-4 py-3 text-sm font-serif text-[#0A1118] leading-relaxed border-t border-gray-200">
                      <strong className="text-[#001F3F]">Figure {f.number || i + 1}{f.title ? `. ${f.title}` : "."}</strong> {f.legend}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </section>
          )}

          {a.references && a.references.length > 0 && (
            <section className="mt-10">
              <h2 className="font-sans text-[11px] tracking-[0.25em] uppercase font-semibold text-[#2BA6A6] mb-3">{t("article.references")}</h2>
              <ol className="list-decimal list-inside space-y-2 font-serif text-sm text-[#0A1118] leading-relaxed">
                {a.references.map((r, i) => <li key={i}>{r}</li>)}
              </ol>
            </section>
          )}

          {a.how_to_cite && (
            <section className="mt-10 border-t border-b border-[#2BA6A6]/40 py-5 bg-[#E8F2F4]/40 px-5">
              <div className="flex items-center gap-2 mb-2">
                <Quote className="w-4 h-4 text-[#2BA6A6]" />
                <h3 className="font-sans text-[11px] tracking-[0.25em] uppercase font-semibold text-[#2BA6A6]">{t("article.how_to_cite")}</h3>
              </div>
              <p className="font-serif text-sm text-[#001F3F]">{a.how_to_cite}</p>
            </section>
          )}

          {a.license && (
            <section className="mt-10 text-xs font-sans text-gray-500">
              <span className="uppercase tracking-widest text-[#2BA6A6] mr-2">{t("article.license")}</span>
              {a.license}
            </section>
          )}
        </div>

        <aside className="lg:col-span-1 lg:border-l lg:border-gray-200 lg:pl-10">
          {a.pdf_url ? (
            <a
              href={assetUrl(a.pdf_url)}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackMetric(slug, "pdf")}
              className="flex items-center justify-center gap-3 bg-[#001F3F] text-white px-5 py-4 text-sm font-sans uppercase tracking-[0.15em] font-semibold hover:bg-[#2BA6A6] transition-colors"
              data-testid="article-pdf-btn"
            >
              <Download className="w-4 h-4" /> {t("article.download_pdf")}
            </a>
          ) : (
            <div className="border border-dashed border-gray-300 px-5 py-4 text-sm font-sans text-gray-500 text-center">
              {t("article.pdf_unavailable")}
            </div>
          )}

          {/* Article Metrics + Share */}
          <div className="mt-6 flex flex-col sm:flex-row lg:flex-col gap-4 items-stretch">
            <div className="flex-1">
              <MetricsPanel metrics={a.metrics} />
            </div>
            <div className="sm:w-44 lg:w-full sm:self-start">
              <ShareMenu article={a} slug={slug} />
            </div>
          </div>

          <div className="mt-8 space-y-4 text-sm font-sans">
            {a.received_date && <Meta label={t("article.received")} value={a.received_date} />}
            {a.accepted_date && <Meta label={t("article.accepted")} value={a.accepted_date} />}
            {a.published_date && <Meta label={t("article.published")} value={a.published_date} />}
            {a.doi && <Meta label={t("article.doi")} value={a.doi} />}
            {a.volume && <Meta label={t("article.volume_issue")} value={`Vol. ${a.volume}, Issue ${a.issue}, ${a.year}`} />}
            {a.pages && <Meta label={t("article.pages")} value={a.pages} />}
          </div>

          {corresp && (
            <div className="mt-8 border-t border-gray-200 pt-6">
              <div className="text-[11px] font-sans uppercase tracking-[0.25em] font-semibold text-[#2BA6A6] mb-3">{t("article.corresponding_author")}</div>
              {corresp.photo_url && (
                <img src={assetUrl(corresp.photo_url)} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-[#2BA6A6] mb-3" data-testid="corresp-photo" />
              )}
              <div className="font-serif text-[#001F3F] text-base mb-1">
                {[corresp.first_name, corresp.middle_name, corresp.last_name].filter(Boolean).join(" ")}
                {cleanDegree(corresp.academic_degree) ? `, ${cleanDegree(corresp.academic_degree)}` : ""}
              </div>
              {corresp.department && <div className="text-xs text-gray-600">{corresp.department}</div>}
              {corresp.institution && <div className="text-xs text-gray-600">{corresp.institution}</div>}
              {(corresp.city || corresp.country) && <div className="text-xs text-gray-600">{[corresp.city, corresp.country].filter(Boolean).join(", ")}</div>}
              {corresp.email && (
                <a href={`mailto:${corresp.email}`} className="mt-2 inline-flex items-center gap-2 text-xs text-[#2BA6A6] hover:text-[#001F3F]">
                  <Mail className="w-3 h-3" /> {corresp.email}
                </a>
              )}
            </div>
          )}

          {a.related && a.related.length > 0 && (
            <div className="mt-10 border-t border-gray-200 pt-6">
              <div className="text-[11px] font-sans uppercase tracking-[0.25em] font-semibold text-[#2BA6A6] mb-4">{t("article.related")}</div>
              <ul className="space-y-4">
                {a.related.map((r) => (
                  <li key={r.slug}>
                    <Link to={`/articles/${r.slug}`} className="font-serif text-sm text-[#001F3F] hover:text-[#2BA6A6] leading-snug block">
                      {r.title}
                    </Link>
                    <div className="text-[11px] text-gray-500 mt-0.5">{r.article_type}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </article>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2BA6A6]">{label}</div>
      <div className="text-sm text-[#001F3F] mt-1">{value}</div>
    </div>
  );
}
